import React, { useEffect, useState, useRef, useCallback } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn } from "@/src/lib/utils";
import { getRecentDrawings } from "@/src/api/drawing";
import { getSignedUrl } from "@/src/api/file";
import { getThumbnailSmallUrl, getPreviewUrl } from "@/src/lib/thumbnails";
import { FilePreviewPopover } from "@/src/components/file/FilePreviewPopover";
import { ImageIcon, CaretLeftIcon, CaretRightIcon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { toast } from "sonner";
import { useJobNotifications } from "@/src/lib/useJobNotifications";

interface JobHistoryProps {
  /** Drawing IDs that are currently being processed (will show shimmer) */
  processingIds?: string[];
}

interface DrawingFile {
  id: string;
  name: string;
  type?: string;
}

interface DrawingJob {
  id: string;
  shareId: string;
  name: string;
  number: string;
  status: 'PROCESSING' | 'COMPLETED' | 'FAILED';
  createdAt: string;
  files: DrawingFile[];
}

// Notification polling interval (Redis-backed, very lightweight)
const NOTIFICATION_POLL_INTERVAL = 5000; // 5 seconds
// Auto-refresh interval for the list (fallback only)
const AUTO_REFRESH_INTERVAL = 60000; // 60 seconds (fallback only)
const PROCESSING_REFRESH_INTERVAL = 10000; // 10 seconds when processing (fallback only)

// Status colors
const STATUS_COLORS = {
  PROCESSING: 'bg-blue-500',
  COMPLETED: 'bg-green-500',
  FAILED: 'bg-red-500',
};

// Shimmer animation component for processing state
function ProcessingShimmer() {
  return (
    <div className="absolute inset-0 overflow-hidden rounded-lg pointer-events-none">
      <div 
        className="absolute inset-0 -translate-x-full"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.4) 50%, transparent 100%)',
          animation: 'shimmer 1.5s infinite',
        }}
      />
      <style>{`
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
      `}</style>
    </div>
  );
}

// Status dot component
function StatusDot({ status }: { status: 'PROCESSING' | 'COMPLETED' | 'FAILED' }) {
  return (
    <div 
      className={cn(
        "absolute top-1 right-1 w-2.5 h-2.5 rounded-full border-2 border-white shadow-sm z-10",
        STATUS_COLORS[status],
        status === 'PROCESSING' && "animate-pulse"
      )}
      title={status}
    />
  );
}

// Individual thumbnail item
function JobThumbnail({ 
  job,
  isProcessing,
}: { 
  job: DrawingJob;
  isProcessing: boolean;
}) {
  const [hasError, setHasError] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const firstFile = job.files?.[0];
  const displayName = job.name || job.number || firstFile?.name || 'Untitled';
  const actualStatus = isProcessing ? 'PROCESSING' : job.status;

  // Load small thumbnail using useQuery for proper caching and retry behavior
  // Note: Thumbnails are generated for ALL files (images are converted to PDF first)
  const smallThumbnailKey = firstFile ? getThumbnailSmallUrl(firstFile.id) : null;
  
  const { data: smallUrl } = useQuery({
    queryKey: ['file', 'thumbnail', 'small', smallThumbnailKey],
    queryFn: async () => {
      if (!smallThumbnailKey) return null;
      const result = await getSignedUrl({ data: { key: smallThumbnailKey } });
      return result?.url ?? null;
    },
    enabled: !!smallThumbnailKey && !hasError,
    staleTime: 1000 * 60 * 50, // 50 min — presigned URLs expire after 1 h
    refetchInterval: (data) => {
      // Retry every 2 seconds until thumbnail is available
      return !data ? 2000 : false;
    },
    refetchIntervalInBackground: false,
  });

  const handleMouseEnter = useCallback((e: React.MouseEvent) => {
    setIsHovered(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setMousePosition({ x: rect.right + 8, y: rect.top });
  }, []);

  return (
    <>
      <Link
        to="/results"
        search={{ shareId: job.shareId }}
        className="block shrink-0"
      >
        <div
          className={cn(
            "relative h-21 shrink-0 inline-flex items-center justify-center rounded-lg overflow-hidden cursor-pointer transition-all duration-200",
            "min-w-21 max-w-[320px]",
            "hover:ring-2 hover:ring-primary hover:ring-offset-2 hover:scale-105",
            "bg-muted border border-border"
          )}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setIsHovered(false)}
        >
          {/* Status dot */}
          <StatusDot status={actualStatus} />
          
          {/* Thumbnail image or placeholder */}
          {smallUrl && !hasError ? (
            <>
              {/* invisible img keeps the container sized to the image aspect ratio */}
              <img
                src={smallUrl}
                alt={displayName}
                className="h-full w-auto opacity-0 pointer-events-none select-none"
                onError={() => setHasError(true)}
              />

              {/* visible cover background eliminates built-in whitespace on extreme aspect ratios */}
              <div
                className="absolute inset-0 bg-center bg-cover"
                style={{ backgroundImage: `url(${smallUrl})` }}
                role="img"
                aria-label={displayName}
              />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <ImageIcon className="w-6 h-6 text-muted-foreground" />
            </div>
          )}
          
          {/* Processing shimmer overlay */}
          {actualStatus === 'PROCESSING' && <ProcessingShimmer />}
        </div>
      </Link>
      
      {/* Hover popover with large thumbnail */}
      {firstFile && (
        <FilePreviewPopover
          fileId={firstFile.id}
          thumbnailKey={getPreviewUrl(firstFile.id)}
          position={mousePosition}
          isVisible={isHovered && !hasError}
          title={displayName}
          onClose={() => setIsHovered(false)}
        />
      )}
    </>
  );
}

export function JobHistory({ processingIds = [] }: JobHistoryProps) {
  const queryClient = useQueryClient();
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  // Track completed drawing IDs to avoid duplicate toast notifications
  const completedDrawingIds = useRef<Set<string>>(new Set());
  // Track which drawings were previously processing
  const previouslyProcessing = useRef<Set<string>>(new Set());

  // Real-time notifications via Redis polling (Vercel-compatible, lightweight)
  useJobNotifications({
    pollInterval: NOTIFICATION_POLL_INTERVAL,
    onJobComplete: () => {
      // Trigger single query refresh instead of constant polling
      queryClient.invalidateQueries({ queryKey: ['recentDrawings'] });
    },
  });

  // Real-time SSE notifications - triggers single poll on job completion
  useJobNotifications({
    onJobComplete: () => {
      // Trigger single poll instead of constant polling
      queryClient.invalidateQueries({ queryKey: ['recentDrawings'] });
     },
  });

  // Invalidate cache when component mounts (e.g., returning from results page)
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['recentDrawings'] });
  }, [queryClient]); // Run once on mount

  // Fetch recent drawings with auto-refresh
  const { data: drawings, isLoading } = useQuery({
    queryKey: ['recentDrawings'],
    queryFn: () => getRecentDrawings({ data: { limit: 10, onlyWithJobs: true } }),
    // Always auto-refresh, faster when processing
    refetchInterval: (query) => {
      const hasProcessing = query.state.data?.some((j: DrawingJob) => j.status === 'PROCESSING');
      const hasProcessingExternal = processingIds.length > 0;
      
      if (hasProcessing || hasProcessingExternal) {
        return PROCESSING_REFRESH_INTERVAL;
      }
      return AUTO_REFRESH_INTERVAL;
    },
    refetchIntervalInBackground: false,
  });

  // Track processing jobs
  useEffect(() => {
    if (!drawings) return;
    
    drawings.forEach((job: DrawingJob) => {
      if (job.status === 'PROCESSING' || processingIds.includes(job.id)) {
        previouslyProcessing.current.add(job.id);
      }
    });
  }, [drawings, processingIds]);

  // Check for newly completed drawings and show toast notifications
  useEffect(() => {
    if (!drawings || drawings.length === 0) return;
    
    drawings.forEach((job: DrawingJob) => {
      // Only notify for completed jobs that we haven't notified about yet
      // AND were previously in processing state
      if (job.status === 'COMPLETED' && !completedDrawingIds.current.has(job.id)) {
        completedDrawingIds.current.add(job.id);
        
        // Only show toast if this job was previously processing
        if (previouslyProcessing.current.has(job.id)) {
          toast.success(`Analysis complete: ${job.name || job.number || 'Drawing'}`, {
            duration: 6000,
            action: {
              label: 'View',
              onClick: () => {
                // Navigate to results page
                window.location.href = `/results?shareId=${job.shareId}`;
              },
            },
          });
        }
      } else if (job.status === 'FAILED' && !completedDrawingIds.current.has(job.id)) {
        completedDrawingIds.current.add(job.id);
        
        // Only show toast if this job was previously processing
        if (previouslyProcessing.current.has(job.id)) {
          toast.error(`Analysis failed: ${job.name || job.number || 'Drawing'}`, {
            duration: 8000,
          });
        }
      }
    });
  }, [drawings]);

  // Check if any job in the list is still processing
  const hasProcessingJobs = drawings?.some(
    (job: DrawingJob) => job.status === 'PROCESSING' || processingIds.includes(job.id)
  );

  // Fallback polling: only active if SSE is disconnected AND jobs are processing
  // Real-time updates come via SSE, this is just a safety net
  useEffect(() => {
    if (hasProcessingJobs) {
      const interval = setInterval(() => {
        queryClient.invalidateQueries({ queryKey: ['recentDrawings'] });
      }, PROCESSING_REFRESH_INTERVAL);
      return () => clearInterval(interval);
    }
  }, [hasProcessingJobs, queryClient]);

  // Check scroll state
  const updateScrollState = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
  }, []);

  useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (el) {
      el.addEventListener('scroll', updateScrollState);
      window.addEventListener('resize', updateScrollState);
      return () => {
        el.removeEventListener('scroll', updateScrollState);
        window.removeEventListener('resize', updateScrollState);
      };
    }
  }, [updateScrollState, drawings]);

  const scroll = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollAmount = 200;
    el.scrollBy({ 
      left: direction === 'left' ? -scrollAmount : scrollAmount, 
      behavior: 'smooth' 
    });
  };

  if (isLoading) {
    return (
      <div className="mt-6">
        <div className="flex gap-3 overflow-x-auto py-2 px-2 -mx-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-21 shrink-0 animate-pulse bg-muted rounded-lg min-w-21" />
          ))}
        </div>
      </div>
    );
  }

  if (!drawings || drawings.length === 0) {
    return null;
  }

  return (
    <div className="mt-6 relative group">
      {/* Left scroll arrow */}
      {canScrollLeft && (
        <button
          onClick={() => scroll('left')}
          className={cn(
            "absolute left-0 top-1/2 -translate-y-1/2 z-10",
            "w-8 h-8 flex items-center justify-center",
            "bg-background/90 border rounded-full shadow-md",
            "opacity-0 group-hover:opacity-100 transition-opacity",
            "hover:bg-muted"
          )}
          aria-label="Scroll left"
        >
          <CaretLeftIcon className="w-4 h-4" />
        </button>
      )}
      
      {/* Scrollable container */}
      <div 
        ref={scrollRef}
        className="flex gap-3 overflow-x-auto py-2 -my-2 scrollbar-hide px-2 -mx-2"
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {drawings.map((job: DrawingJob) => {
          const isProcessing = job.status === 'PROCESSING' || processingIds.includes(job.id);
          
          return (
            <JobThumbnail
              key={job.id}
              job={job}
              isProcessing={isProcessing}
            />
          );
        })}
      </div>
      
      {/* Right scroll arrow */}
      {canScrollRight && (
        <button
          onClick={() => scroll('right')}
          className={cn(
            "absolute right-0 top-1/2 -translate-y-1/2 z-10",
            "w-8 h-8 flex items-center justify-center",
            "bg-background/90 border rounded-full shadow-md",
            "opacity-0 group-hover:opacity-100 transition-opacity",
            "hover:bg-muted"
          )}
          aria-label="Scroll right"
        >
          <CaretRightIcon className="w-4 h-4" />
        </button>
      )}
    </div>
  );
}
